import React, { useState, useRef, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { ChevronLeft, ChevronRight, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from 'lib/utils';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import { getUserJourney } from '../../../services/api';
import LoadingState from '../../shared/LoadingState';

// Hand-rolled sticky-header table classes (house convention — mirrors data-table).
const TH = 'sticky top-0 z-[3] border-b border-borderGrey bg-white p-2 text-left font-semibold whitespace-nowrap';
const TH_NUM = cn(TH, 'text-right');
const TD = 'border-b border-borderGrey p-2';
const TD_SM = cn(TD, 'text-token-sm');
const TD_NUM = cn(TD, 'text-right');

function CopyEmailButton({ email }) {
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(email);
      toast.success('Email copied');
    } catch (err) {
      toast.error('Failed to copy email');
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      title="Copy email"
      className="text-muted-foreground transition-colors hover:text-foreground"
    >
      <Copy size={12} aria-hidden="true" />
    </button>
  );
}

export default function ChurnTab({ churn, churnLoading, churnTrend, initialWpId }) {
  const [userJourney, setUserJourney] = useState(null);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [selectedWpId, setSelectedWpId] = useState(null);
  const [userListView, setUserListView] = useState('active');
  const journeyRef = useRef(null);

  // Deep-link from the Retention tab watchlist: auto-open that user's journey
  useEffect(() => {
    if (initialWpId) {
      handleUserClick(initialWpId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialWpId]);

  const handleUserClick = async (wpId) => {
    setSelectedWpId(wpId);
    setJourneyLoading(true);
    setUserJourney(null);
    journeyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    try {
      const res = await getUserJourney(wpId);
      setUserJourney(res);
    } catch (err) {
      console.error('Error fetching user journey:', err);
      toast.error('Failed to load user journey');
    }
    setJourneyLoading(false);
  };

  if (churnLoading || !churn) {
    return <LoadingState message="Loading churn analysis..." />;
  }

  // Prev/next stepping through the currently displayed user list.
  const currentList = userListView === 'churned'
    ? (churn.churned_users || [])
    : (churn.active_users || []);
  const currentIndex = selectedWpId != null
    ? currentList.findIndex((u) => u.wp_id === selectedWpId)
    : -1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex !== -1 && currentIndex < currentList.length - 1;

  const comparisonMetrics = [
    {
      label: 'Avg Sessions/Week',
      churned: churn.churned?.avg_sessions_per_week || 0,
      retained: churn.retained?.avg_sessions_per_week || 0,
    },
    {
      label: 'Avg Pages/Week',
      churned: churn.churned?.avg_pages_per_week || 0,
      retained: churn.retained?.avg_pages_per_week || 0,
    },
    {
      label: 'Avg Engagement/Week',
      churned: `${(churn.churned?.avg_engagement_per_week || 0).toFixed(0)}s`,
      retained: `${(churn.retained?.avg_engagement_per_week || 0).toFixed(0)}s`,
    },
  ];

  return (
    <div className="w-full min-w-0">
      {/* Monthly Churn Trend */}
      {churnTrend && churnTrend.length > 0 && (
        <div className="mb-4 rounded-token-large border border-borderGrey bg-white p-4">
          <h3 className="mb-2 text-token-xl font-semibold">Monthly Churn Trend</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={churnTrend}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#DADADA" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="churned" fill="#f44336" name="Churned Users" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Churned vs Retained comparison — compact stat table */}
      <div className="mb-4 rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="mb-2 text-token-xl font-semibold">Churned vs Retained User Behavior</h3>
        <table className="w-auto min-w-[420px] border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className="border-b border-borderGrey p-2" />
              <th className="border-b border-borderGrey p-2 text-right font-semibold text-warning">
                Churned ({churn.churned?.user_count || 0})
              </th>
              <th className="border-b border-borderGrey p-2 text-right font-semibold text-success">
                Active ({churn.retained?.user_count || 0})
              </th>
            </tr>
          </thead>
          <tbody>
            {comparisonMetrics.map((m) => (
              <tr key={m.label}>
                <td className="border-b border-borderGrey p-2 text-muted-foreground">{m.label}</td>
                <td className="border-b border-borderGrey p-2 text-right font-semibold">{m.churned}</td>
                <td className="border-b border-borderGrey p-2 text-right font-semibold">{m.retained}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Users List */}
      <div className="mb-4 rounded-token-large border border-borderGrey bg-white p-4">
        <div className="mb-2 flex items-center gap-3">
          <h3 className="text-token-xl font-semibold">Users</h3>
          <ToggleGroup
            type="single"
            size="sm"
            value={userListView}
            onValueChange={(v) => v && setUserListView(v)}
          >
            <ToggleGroupItem value="churned">Churned</ToggleGroupItem>
            <ToggleGroupItem value="active">Active</ToggleGroupItem>
          </ToggleGroup>
        </div>

        {userListView === 'churned' && churn.churned_users && churn.churned_users.length > 0 && (
          <div className="overflow-auto rounded-token-md border border-borderGrey" style={{ maxHeight: 400 }}>
            <table className="w-full border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  <th className={TH}>WP ID</th>
                  <th className={TH}>Email</th>
                  <th className={TH}>Canceled</th>
                  <th className={TH}>Duration</th>
                  <th className={TH}>Last Active</th>
                  <th className={TH_NUM}>Sessions/Wk</th>
                  <th className={TH_NUM}>Pages/Wk</th>
                  <th className={TH_NUM}>Engagement/Wk</th>
                  <th className={TH_NUM}>Total Events</th>
                </tr>
              </thead>
              <tbody>
                {churn.churned_users.map((user) => (
                  <tr
                    key={user.wp_id}
                    onClick={() => handleUserClick(user.wp_id)}
                    className={cn(
                      'cursor-pointer transition-colors hover:bg-neutral-50',
                      selectedWpId === user.wp_id && 'bg-neutral-100'
                    )}
                  >
                    <td className={TD}>{user.wp_id}</td>
                    <td className={TD_SM}>
                      <span className="inline-flex items-center gap-1.5">
                        {user.email}
                        <CopyEmailButton email={user.email} />
                      </span>
                    </td>
                    <td className={TD_SM}>{user.canceled_at}</td>
                    <td className={TD_SM}>
                      {user.subscription_duration_days != null
                        ? user.subscription_duration_days >= 60
                          ? `${Math.round(user.subscription_duration_days / 30)}mo`
                          : `${user.subscription_duration_days}d`
                        : '—'}
                    </td>
                    <td className={TD_SM}>{user.last_active || '—'}</td>
                    <td className={TD_NUM}>{user.sessions_per_week}</td>
                    <td className={TD_NUM}>{user.pages_per_week}</td>
                    <td className={TD_NUM}>{user.engagement_per_week.toFixed(0)}s</td>
                    <td className={TD_NUM}>{user.total_events}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {userListView === 'active' && churn.active_users && churn.active_users.length > 0 && (
          <div className="overflow-auto rounded-token-md border border-borderGrey" style={{ maxHeight: 400 }}>
            <table className="w-full border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  <th className={TH}>WP ID</th>
                  <th className={TH}>Email</th>
                  <th className={TH}>Product</th>
                  <th className={TH}>Type</th>
                  <th className={TH}>Start Date</th>
                  <th className={TH_NUM}>Sessions/Wk</th>
                  <th className={TH_NUM}>Pages/Wk</th>
                  <th className={TH}>Last Active</th>
                </tr>
              </thead>
              <tbody>
                {churn.active_users.map((user) => (
                  <tr
                    key={user.wp_id}
                    onClick={() => handleUserClick(user.wp_id)}
                    className={cn(
                      'cursor-pointer transition-colors hover:bg-neutral-50',
                      selectedWpId === user.wp_id && 'bg-neutral-100'
                    )}
                  >
                    <td className={TD}>{user.wp_id}</td>
                    <td className={TD_SM}>
                      <span className="inline-flex items-center gap-1.5">
                        {user.email}
                        <CopyEmailButton email={user.email} />
                      </span>
                    </td>
                    <td className={TD_SM}>{user.product_name}</td>
                    <td className={TD_SM}>{user.subscription_type}</td>
                    <td className={TD_SM}>{user.start_date}</td>
                    <td className={TD_NUM}>{user.sessions_per_week}</td>
                    <td className={TD_NUM}>{user.pages_per_week}</td>
                    <td className={TD_SM}>{user.last_active || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {userListView === 'active' && (!churn.active_users || churn.active_users.length === 0) && (
          <p className="p-4 text-center text-token-md text-muted-foreground">No active subscribers found.</p>
        )}
        {userListView === 'churned' && (!churn.churned_users || churn.churned_users.length === 0) && (
          <p className="p-4 text-center text-token-md text-muted-foreground">No churned users found.</p>
        )}
      </div>

      {/* User Journey — shown when a user is selected from the table */}
      {(selectedWpId || journeyLoading) && (
        <div ref={journeyRef} className="rounded-token-large border border-borderGrey bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <h3 className="text-token-xl font-semibold">
              User Journey {selectedWpId ? `— WP ID: ${selectedWpId}` : ''}
            </h3>
            <div className="ml-auto flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => hasPrev && handleUserClick(currentList[currentIndex - 1].wp_id)}
                disabled={!hasPrev || journeyLoading}
                title="Previous user in list"
              >
                <ChevronLeft />
              </Button>
              {currentIndex !== -1 && (
                <span className="text-token-base text-muted-foreground">
                  {currentIndex + 1} / {currentList.length}
                </span>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => hasNext && handleUserClick(currentList[currentIndex + 1].wp_id)}
                disabled={!hasNext || journeyLoading}
                title="Next user in list"
              >
                <ChevronRight />
              </Button>
            </div>
          </div>

          {journeyLoading && !userJourney && (
            <div className="flex items-center gap-2 py-2">
              <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
              <p className="text-token-md text-muted-foreground">Loading user journey...</p>
            </div>
          )}

          {userJourney && (
            <div>
              <div className="mb-3 flex flex-wrap gap-1.5">
                <Badge variant="outline">WP ID: {userJourney.wp_id}</Badge>
                {userJourney.subscription && (
                  <>
                    <Badge variant={userJourney.subscription.status === 'active' ? 'success' : 'outline'}>
                      Status: {userJourney.subscription.status}
                    </Badge>
                    <Badge variant="outline">
                      Type: {userJourney.subscription.subscription_type || 'N/A'}
                    </Badge>
                    {userJourney.subscription.canceled_at && (
                      <Badge variant="destructive">Canceled: {userJourney.subscription.canceled_at}</Badge>
                    )}
                  </>
                )}
              </div>

              {(() => {
                const pvs = userJourney.page_views || [];
                const grouped = {};
                pvs.forEach((pv) => {
                  if (!grouped[pv.date]) grouped[pv.date] = [];
                  grouped[pv.date].push(pv);
                });
                const dates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));
                let prevSessionId = null;
                let sessionColorIdx = 0;
                const sessionColors = ['bg-neutral-100', 'bg-white'];

                return dates.map((date) => (
                  <div key={date} className="mb-4">
                    <p className="mb-1 text-token-md font-semibold">{date}</p>
                    <div className="overflow-auto rounded-token-md border border-borderGrey" style={{ maxHeight: 400 }}>
                      <table className="w-full border-separate border-spacing-0 text-sm">
                        <thead>
                          <tr>
                            <th className={cn(TH, 'w-16')}>Time</th>
                            <th className={cn(TH, 'w-20')}>Site</th>
                            <th className={TH}>Page</th>
                            <th className={TH}>Events</th>
                          </tr>
                        </thead>
                        <tbody>
                          {grouped[date].map((pv, i) => {
                            if (pv.session_id !== prevSessionId) {
                              sessionColorIdx = 1 - sessionColorIdx;
                              prevSessionId = pv.session_id;
                            }
                            const time = pv.timestamp
                              ? new Date(pv.timestamp / 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                              : '';
                            return (
                              <tr key={i} className={sessionColors[sessionColorIdx]}>
                                <td className={TD_SM}>{time}</td>
                                <td className={TD}>
                                  <Badge
                                    variant={pv.property === 'web_app' ? 'info' : 'success'}
                                    className="px-1.5 py-0 text-token-xs"
                                  >
                                    {pv.property === 'web_app' ? 'App' : 'Site'}
                                  </Badge>
                                </td>
                                <td className={cn(TD, 'font-mono text-token-sm')}>{pv.page_path}</td>
                                <td className={TD}>
                                  <div className="flex flex-wrap gap-1">
                                    {(pv.events || []).map((evt, j) => (
                                      <Badge key={j} variant="outline" className="px-1.5 py-0 text-token-xs font-normal">
                                        {evt.event}{evt.label ? `: ${evt.label}` : ''}
                                      </Badge>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
